import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {LocationCardScratchpadState} from '../../scratchpad/locationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {LocationTargetingMethodValue} from 'common/src/modules/locationFeed/locationFeed.types';
import locationFeedItemsApi from 'common/src/modules/locationFeedItems/locationFeedItems.api';

const setTopLocationsToolSchema = z.object({
  locationIds: z
    .array(z.string())
    .describe('Array of specific location IDs to use for Specific Store targeting'),
});

export default function setTopLocationsTool() {
  async function execute(
    params: ToolExecuteParams<typeof setTopLocationsToolSchema, LocationCardScratchpadState>,
  ) {
    const {locationIds} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<LocationCardScratchpadState>(
      agentScratchpad,
      NodeType.STORE,
    );

    if (!locationIds) {
      return toolResult('No location IDs provided');
    }

    if (
      validatedScratchpad.card.locationTargetingMethod !==
      LocationTargetingMethodValue.SpecificStore
    ) {
      return toolResult(
        'Specific location ids should be set only for Specific Store targeting method. Either set the location targeting method to Specific Store or remove the specific location ids.',
      );
    }

    if (locationIds.length === 0) {
      return toolResult('No location IDs provided');
    }

    const locations = await locationFeedItemsApi.getLocationsByIds(
      validatedScratchpad.card.locationFeedId,
      locationIds,
    );

    const existingLocations = locations.filter((location) => location !== null);
    const existingLocationIds = existingLocations.map((location) => location.id);
    const nonExistingIds = locationIds.filter((id) => !existingLocationIds.includes(id));

    if (existingLocations.length === 0) {
      return toolResult('None of the provided location IDs exist in the feed');
    }

    validatedScratchpad.card.topLocationIds = existingLocationIds;

    if (nonExistingIds.length > 0) {
      return toolResult(
        `Top locations set to: ${existingLocationIds.join(', ')}. The following IDs were not found and were not set: ${nonExistingIds.join(', ')}`,
      );
    }

    return toolResult(`Top locations set to: ${existingLocationIds.join(', ')}`);
  }

  return tool({
    name: V2CardAgentToolName.SetTopLocations,
    displayName: 'Setting top locations',
    description: 'Set specific location IDs for Specific Store targeting',
    execute,
    inputSchema: setTopLocationsToolSchema,
  });
}
